import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { berlinYearMonth } from "../utils/projection";
import { useEarliestComputedMonth } from "./useEarliestComputedMonth";

export interface SelectedMonth {
  /** "YYYY-MM", always valid and in range. */
  month: string;
  /** Clamps, then updates the URL (`?month=`). */
  setMonth: (m: string) => void;
  /** Back to the current Berlin month; removes `?month=`. */
  reset: () => void;
  currentMonth: string;
  /** Earliest computed month, null if unknown (no lower bound). */
  earliestMonth: string | null;
  isCurrentMonth: boolean;
}

const MONTH_PARAM = "month";
const YEAR_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * The page-level month, kept in the URL so a view can be shared and survives reload.
 *
 * The default is the CURRENT Berlin month (BOF-82). An invalid, future or too-early value never throws:
 * a future month falls back to the current one, a month before the earliest computed one clamps to it.
 * `YYYY-MM` strings compare correctly as plain strings.
 */
export function useSelectedMonth(now: Date = new Date()): SelectedMonth {
  const [searchParams, setSearchParams] = useSearchParams();
  const earliestMonth = useEarliestComputedMonth();
  const currentMonth = berlinYearMonth(now);

  const clamp = useCallback(
    (candidate: string | null): string => {
      if (!candidate || !YEAR_MONTH.test(candidate) || candidate > currentMonth) return currentMonth;
      if (earliestMonth && candidate < earliestMonth) return earliestMonth;
      return candidate;
    },
    [currentMonth, earliestMonth],
  );

  const month = useMemo(() => clamp(searchParams.get(MONTH_PARAM)), [clamp, searchParams]);

  const setMonth = useCallback(
    (m: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(MONTH_PARAM, clamp(m));
          return next;
        },
        { replace: true },
      );
    },
    [clamp, setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(MONTH_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return { month, setMonth, reset, currentMonth, earliestMonth, isCurrentMonth: month === currentMonth };
}
